import React from 'react';

/**
 * DeliverablesSection Component
 * 
 * Displays deliverables conditions (required file outputs) with submission status.
 * Shows which files must be submitted to complete this mission.
 * 
 * Used by:
 * - MetadataSection (Mission Full View sidebar) - stacked layout
 * - ConditionsModal (from card toolbar) - side-by-side layout
 */
const DeliverablesSection = ({
  task,
  onEditDeliverables,
  onSubmitDeliverable,
  onClearDeliverable,
  className = '',
}) => {
  // Deliverables conditions (required file outputs)
  const deliverables = task.deliverables || [];
  const hasDeliverables = deliverables.length > 0;
  const completedDeliverables = deliverables.filter(d => d.completed).length;

  return (
    <div className={`mfv-metadata__group mfv-metadata__group--deliverables ${className}`}>
      {/* Section header */}
      <div className="mfv-metadata__section-header">
        <h3 className="mfv-metadata__section-title">Deliverables</h3>
        <div className="mfv-metadata__section-header-right">
          {hasDeliverables && (
            <span className="mfv-metadata__deliverables-progress">
              {completedDeliverables}/{deliverables.length}
            </span>
          )}
          {onEditDeliverables && (
            <button 
              className="mfv-metadata__edit-btn"
              onClick={() => onEditDeliverables(task.id)}
              type="button"
            >
              {hasDeliverables ? 'Edit' : 'Add'}
            </button>
          )}
        </div>
      </div>
      
      {hasDeliverables ? (
        <>
          <div className="mfv-metadata__deliverables">
            {deliverables.map((deliverable, index) => {
              const isComplete = deliverable.completed;
              return (
                <div 
                  key={deliverable.id || index} 
                  className={`mfv-metadata__deliverable ${isComplete ? 'mfv-metadata__deliverable--complete' : ''}`}
                >
                  <span className={`mfv-metadata__deliverable-check ${isComplete ? 'mfv-metadata__deliverable-check--complete' : ''}`}>
                    {isComplete ? '✓' : '○'}
                  </span>
                  <div className="mfv-metadata__deliverable-content">
                    <span className={`mfv-metadata__deliverable-name ${isComplete ? 'mfv-metadata__deliverable-name--complete' : ''}`}>
                      {deliverable.name}
                    </span>
                    {deliverable.fileType && (
                      <span className="mfv-metadata__deliverable-type">{deliverable.fileType}</span>
                    )}
                    {/* Show submitted info if complete */}
                    {isComplete && deliverable.submittedFilename && (
                      <span className="mfv-metadata__deliverable-submitted">
                        Submitted: {deliverable.submittedFilename}
                      </span>
                    )}
                    {isComplete && deliverable.link && !deliverable.submittedFilename && (
                      <a 
                        href={deliverable.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mfv-metadata__deliverable-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deliverable.link}
                      </a>
                    )}
                  </div>
                  {/* Action buttons */}
                  <div className="mfv-metadata__deliverable-actions">
                    {/* Submit button (when not complete) */}
                    {!isComplete && onSubmitDeliverable && (
                      <button
                        className="mfv-metadata__deliverable-submit"
                        onClick={() => onSubmitDeliverable(deliverable)}
                        title="Submit this deliverable"
                        type="button"
                      >
                        Submit
                      </button>
                    )}
                    
                    {/* Change button (when complete) */}
                    {isComplete && onSubmitDeliverable && (
                      <button
                        className="mfv-metadata__deliverable-submit mfv-metadata__deliverable-submit--update"
                        onClick={() => onSubmitDeliverable(deliverable)}
                        title={deliverable.attachmentId ? "Upload a new file to replace the current one" : "Change the submitted link"}
                        type="button"
                      >
                        {deliverable.attachmentId ? 'Change File' : 'Change Link'}
                      </button>
                    )}
                    
                    {/* Clear button (when complete) */}
                    {isComplete && onClearDeliverable && (
                      <button
                        className="mfv-metadata__deliverable-clear"
                        onClick={() => onClearDeliverable(deliverable)}
                        title={deliverable.attachmentId ? "Remove the file link (the file stays in attachments)" : "Clear the submitted link"}
                        type="button"
                      >
                        {deliverable.attachmentId ? 'Unlink File' : 'Clear Link'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="mfv-metadata__empty-state">
          No deliverables
        </p>
      )}
    </div>
  );
};

export default DeliverablesSection;
